// SPDX-License-Identifier: LicenseRef-Proprietary
import React, { useState, useCallback, useEffect, useRef } from "react";
import { REGIONS } from "../lib/models";

interface RegionSelectorProps {
  selectedRegions: string[];
  onRegionsChange: (regions: string[]) => void;
  "aria-label"?: string;
}

export const RegionSelector: React.FC<RegionSelectorProps> = ({
  selectedRegions = [],
  onRegionsChange,
  "aria-label": ariaLabel,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  
  // Unified close handler with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    // Wait for animation to complete before removing from DOM
    setTimeout(() => {
      setIsExpanded(false);
      setIsClosing(false);
    }, 250);
  }, []);
  
  // Toggle handler
  const handleToggle = useCallback(() => {
    if (isExpanded) {
      handleClose();
    } else {
      setIsExpanded(true);
    }
  }, [isExpanded, handleClose]);
  
  // Esc key handler
  useEffect(() => {
    if (!isExpanded) return;
    
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isExpanded, handleClose]);
  
  // Click outside handler (for scrim and clicks outside panel)
  useEffect(() => {
    if (!isExpanded) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      // Don't close if clicking inside panel or on trigger button
      if (
        panelRef.current?.contains(target) ||
        triggerRef.current?.contains(target)
      ) {
        return;
      }
      handleClose();
    };
    
    // Small delay to avoid immediate close on open
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, handleClose]);
  const handleRegionToggle = (regionKey: string) => {
    if (!onRegionsChange) return;
    
    const currentRegions = selectedRegions || [];
    const isFinlandOnlySelected = currentRegions.includes("finland") && currentRegions.length === 1;
    
    if (regionKey === "finland") {
      // If Finland is the only selected region, clicking it does nothing (can't deselect the last region)
      // Otherwise, select only Finland
      if (!isFinlandOnlySelected) {
        onRegionsChange(["finland"]);
      }
    } else {
      // If Finland is the only selected region, deselect Finland and select this region
      if (isFinlandOnlySelected) {
        onRegionsChange([regionKey]);
      } else {
        // Normal toggle: remove Finland if present, then toggle the region
        const newRegions = currentRegions.filter((r) => r !== "finland");

        if (newRegions.includes(regionKey)) {
          // Remove the region
          const updatedRegions = newRegions.filter((r) => r !== regionKey);
          // If no regions left, select Finland as fallback
          onRegionsChange(updatedRegions.length > 0 ? updatedRegions : ["finland"]);
        } else {
          // Add the region
          onRegionsChange([...newRegions, regionKey]);
        }
      }
    }
  };

  const handleSelectAll = () => {
    if (onRegionsChange) {
      onRegionsChange(REGIONS.map((r) => r.key));
    }
  };

  const handleClearAll = () => {
    if (onRegionsChange) {
      onRegionsChange([]);
    }
  };

  const isFinlandSelected = (selectedRegions || []).includes("finland");
  const selectedCount = (selectedRegions || []).length;

  return (
    <>
      {/* Overlay/Scrim */}
      {(isExpanded || isClosing) && (
        <div 
          className={`vaccine-demo__region-overlay ${isClosing ? 'vaccine-demo__region-overlay--closing' : ''}`}
          onClick={handleClose}
          aria-hidden="true"
        />
      )}
      
      <div className="vaccine-demo__region-selector">
        <div className="vaccine-demo__region-selector-header">
          <label className="vaccine-demo__label" htmlFor="region-selector">
            Target regions
          </label>
          <button
            ref={triggerRef}
            type="button"
            onClick={handleToggle}
            className="vaccine-demo__region-trigger"
            aria-expanded={isExpanded && !isClosing}
            aria-controls="region-selector-panel"
          >
            {(isExpanded && !isClosing) ? 'Hide regions ⌃' : 'Select regions ⌄'}
          </button>
        </div>

        {/* Panel */}
        {(isExpanded || isClosing) && (
          <div
            id="region-selector-panel"
            ref={panelRef}
            className={`vaccine-demo__region-panel ${isClosing ? 'vaccine-demo__region-panel--closing' : ''}`}
          >
            {/* Panel Header */}
            <div className="vaccine-demo__region-panel-header">
              <h3 className="vaccine-demo__region-panel-title">Select regions</h3>
              <button
                type="button"
                onClick={handleClose}
                className="vaccine-demo__region-panel-close"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            
            {/* Panel Content */}
            <div className="vaccine-demo__region-panel-content">
              <div className="vaccine-demo__region-selector-controls">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="btn btn-secondary btn-sm"
                  disabled={selectedCount === REGIONS.length}
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="btn btn-secondary btn-sm"
                  disabled={selectedCount === 0}
                >
                  Clear all
                </button>
              </div>
              {selectedCount > 0 && (
                <span className="vaccine-demo__region-count">
                  {selectedCount} region{selectedCount !== 1 ? "s" : ""} selected
                </span>
              )}

              <div
                className="vaccine-demo__region-grid"
                role="group"
                aria-label={ariaLabel || "Select target regions"}
              >
                {REGIONS.map((region) => {
                  const isSelected = (selectedRegions || []).includes(region.key);

                  return (
                    <button
                      key={region.key}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRegionToggle(region.key);
                      }}
                      className={`vaccine-demo__region-option ${
                        isSelected ? "vaccine-demo__region-option--selected" : ""
                      }`}
                      aria-pressed={isSelected}
                      aria-label={`${isSelected ? "Deselect" : "Select"} ${
                        region.name
                      }`}
                    >
                      <div className="vaccine-demo__region-option-name">
                        {region.name}
                      </div>
                      {isSelected && (
                        <div className="vaccine-demo__region-option-check">✓</div>
                      )}
                    </button>
                  );
                })}
              </div>

              {isFinlandSelected && (
                <div className="vaccine-demo__region-note">
                  Finland (entire country) is selected. Click on a specific region to switch to regional selection.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
